import PropTypes from 'prop-types';
import React from 'react';
import { I18n } from 'react-redux-i18n';
import getSubdomain from 'utils/getSubdomain';

const originalTranslate = I18n.t.bind(I18n);

const replace = {
  'saint-gobain': 'Connecti@',
}[getSubdomain()];

I18n.t = (key, options) => {
  const translation = originalTranslate(key, options);
  return replace ? translation.replace(/Ommnio/g, replace) : translation;
};

const getRawTranslation = (translationLocale, key, options = {}) => {
  try {
    return I18n._fetchTranslation(I18n._translations, `${translationLocale}.${key}`, options.count);
  } catch {
    return I18n._handleMissingTranslation(key, options);
  }
};

const getTranslation = (translationLocale, key, replacements = {}) => {
  try {
    const translation = getRawTranslation(translationLocale, key, replacements);
    const translated = I18n._replace(translation, replacements);
    return translated && replace ? translated.replace(/Ommnio/g, replace) : translated;
  } catch {
    return I18n._handleMissingTranslation(key, replacements);
  }
};

const PLACEHOLDER_RE = /%\{(\w+)\}/g;
const RICH_TAG_RE = /<(\w+)>([^<]*)<\/\1>/g;

const getPlaceholderKeys = translationString => {
  const keys = new Set();
  let m;
  PLACEHOLDER_RE.lastIndex = 0;
  while ((m = PLACEHOLDER_RE.exec(translationString)) !== null) keys.add(m[1]);
  return keys;
};

const getRichTagNames = translationString => {
  const names = new Set();
  let m;
  RICH_TAG_RE.lastIndex = 0;
  while ((m = RICH_TAG_RE.exec(translationString)) !== null) names.add(m[1]);
  return names;
};

const applyOmmnioReplace = str =>
  typeof str === 'string' && replace ? str.replace(/Ommnio/g, replace) : str;

/**
 * React-intl-style rich values: translation string can use <tagName>content</tagName>.
 * When values[tagName] is a function (chunks) => ReactNode, content is substituted
 * for %{key} then passed to it. E.g. "<b>{organizationName}</b> requires..." with
 * values={{ organizationName: 'Acme', b: chunks => <strong>{chunks}</strong> }}.
 */
const replaceWithRichValues = (translationString, values) => {
  const chunks = [];
  let lastIndex = 0;
  let m;
  RICH_TAG_RE.lastIndex = 0;
  while ((m = RICH_TAG_RE.exec(translationString)) !== null) {
    const textBefore = translationString.slice(lastIndex, m.index);
    if (textBefore) {
      const replaced = I18n._replace(textBefore, values);
      chunks.push(applyOmmnioReplace(replaced));
    }
    const tagName = m[1];
    const content = m[2];
    const inner = I18n._replace(content, values);
    const wrapper = values[tagName];
    if (typeof wrapper === 'function') {
      chunks.push(wrapper(inner));
    } else {
      chunks.push(applyOmmnioReplace(inner));
    }
    lastIndex = m.index + m[0].length;
  }
  const tail = translationString.slice(lastIndex);
  if (tail) {
    const replaced = I18n._replace(tail, values);
    chunks.push(applyOmmnioReplace(replaced));
  }
  return chunks;
};

/**
 * Splits a translation string by %{key} placeholders and interleaves replacement values
 * (strings, numbers, or React elements). Use when placeholders are React elements.
 */
const chunkTranslation = (translationString, replacements) => {
  const chunks = [];
  let lastIndex = 0;
  let m;
  PLACEHOLDER_RE.lastIndex = 0;
  while ((m = PLACEHOLDER_RE.exec(translationString)) !== null) {
    chunks.push(applyOmmnioReplace(translationString.slice(lastIndex, m.index)));
    const key = m[1];
    const val = replacements[key];
    chunks.push(val !== undefined ? val : `%{${key}}`);
    lastIndex = m.index + m[0].length;
  }
  chunks.push(applyOmmnioReplace(translationString.slice(lastIndex)));
  return chunks;
};

const hasElementChunks = replacements =>
  Object.keys(replacements).length > 0 &&
  Object.values(replacements).some(v => React.isValidElement(v));

const hasRichValues = replacements =>
  Object.keys(replacements).length > 0 &&
  Object.values(replacements).some(v => typeof v === 'function');

const Translate = ({
  value,
  dangerousHTML = false,
  tag: Tag = 'span',
  locale = null,
  ...otherProps
}) => {
  if (dangerousHTML) {
    const translation = locale
      ? getTranslation(locale, value, otherProps)
      : I18n.t(value, otherProps);
    return <Tag dangerouslySetInnerHTML={{ __html: translation }} {...otherProps} />;
  }

  const useRichValues = hasRichValues(otherProps);
  const useChunks = !useRichValues && hasElementChunks(otherProps);

  if (!useRichValues && !useChunks) {
    const translation = locale
      ? getTranslation(locale, value, otherProps)
      : I18n.t(value, otherProps);
    return <Tag {...otherProps}>{translation}</Tag>;
  }

  const raw = locale
    ? getRawTranslation(locale, value, otherProps)
    : (() => {
        const t = I18n._translations;
        const loc = I18n._locale;
        const translationLocale = t?.[loc] ? loc : (loc || '').split('-')[0];
        return I18n._fetchTranslation(t, `${translationLocale}.${value}`, otherProps.count);
      })();

  const valueKeys = useRichValues
    ? new Set([...getPlaceholderKeys(raw), ...getRichTagNames(raw)])
    : getPlaceholderKeys(raw);
  const tagProps = Object.fromEntries(
    Object.entries(otherProps).filter(([k]) => !valueKeys.has(k)),
  );

  const chunks = useRichValues
    ? replaceWithRichValues(raw, otherProps)
    : chunkTranslation(raw, otherProps);
  return <Tag {...tagProps}>{chunks}</Tag>;
};

Translate.propTypes = {
  value: PropTypes.string.isRequired,
  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  dangerousHTML: PropTypes.bool,
  locale: PropTypes.string,
};

export { I18n, Translate };
